import ApiService from "@/core/services/ApiService";
import axios from "axios";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { BackendError } from "../models";

export interface Justificacion2025 {
  id?: string;
  l1CreditoComprometido: number | null;
  l1CreditoEjecutado: number | null;
  l2CreditoComprometido: number | null;
  l2CreditoEjecutado: number | null;
  l3CreditoComprometido: number | null;
  l3CreditoEjecutado: number | null;
  l4CreditoComprometido: number | null;
  l4CreditoEjecutado: number | null;
  l5CreditoComprometido: number | null;
  l5CreditoEjecutado: number | null;
  comarcaId?: string;
  comarca?: {
    id?: string;
    nombre: string;
  };
}

@Module({ namespaced: true, name: "Justificacion2025Module" })
export class Justificacion2025Module extends VuexModule {
  errors: BackendError | null = null;
  justificacion: Justificacion2025 | null = null;
  justificaciones: Justificacion2025[] = [];
  isNew = true;

  @Action({ rawError: true })
  async fetchByComarcaId(comarcaId: string) {
    try {
      const url = `/comarcas/${comarcaId}/justificacion-inicial-2025`;
      const { data } = await ApiService.get(url);
      const justificacion: Justificacion2025 = { ...data };
      this.setJustificacion(justificacion);
      this.setIsNew(false);
      return justificacion;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        // Si es 404, significa que no hay datos aún (es nuevo)
        if (err.response?.status === 404) {
          this.setIsNew(true);
          this.setJustificacion(null);
          return null;
        }
        if (err.response && err.response.data) {
          this.setError(err.response.data as BackendError);
        } else {
          this.setError(null);
        }
      } else {
        this.setError(null);
      }
      throw err;
    }
  }

  @Action({ rawError: true })
  async create(payload: {
    comarcaId: string;
    justificacion: Justificacion2025;
  }) {
    try {
      const url = `/comarcas/${payload.comarcaId}/justificacion-inicial-2025`;
      const dataToSend = { ...payload.justificacion };
      const { data } = await ApiService.post(url, dataToSend);
      this.setJustificacion(data);
      this.setIsNew(false);
      this.setError(null);
      return data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response && err.response.data) {
          this.setError(err.response.data as BackendError);
        } else {
          this.setError(null);
        }
      } else {
        this.setError(null);
      }
      throw err;
    }
  }

  @Action({ rawError: true })
  async update(payload: {
    comarcaId: string;
    justificacion: Justificacion2025;
  }) {
    try {
      const url = `/comarcas/${payload.comarcaId}/justificacion-inicial-2025`;
      const dataToSend = { ...payload.justificacion };
      const { data } = await ApiService.put(url, dataToSend);
      this.setJustificacion(data);
      this.setError(null);
      return data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response && err.response.data) {
          this.setError(err.response.data as BackendError);
        } else {
          this.setError(null);
        }
      } else {
        this.setError(null);
      }
      throw err;
    }
  }

  @Action({ rawError: true })
  async fetchAll() {
    try {
      const url = `/justificacion-inicial-2025`;
      const { data } = await ApiService.get(url);
      const justificaciones: Justificacion2025[] = Array.isArray(data)
        ? data
        : [];
      this.setJustificaciones(justificaciones);
      this.setError(null);
      return justificaciones;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response && err.response.data) {
          this.setError(err.response.data as BackendError);
        } else {
          this.setError(null);
        }
      } else {
        this.setError(null);
      }
      throw err;
    }
  }

  @Mutation
  setJustificacion(data: Justificacion2025 | null) {
    this.justificacion = data;
  }

  @Mutation
  setJustificaciones(data: Justificacion2025[]) {
    this.justificaciones = data;
  }

  @Mutation
  setIsNew(isNew: boolean) {
    this.isNew = isNew;
  }

  @Mutation
  setError(error: BackendError | null) {
    this.errors = error;
  }
}
